/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Layer } from "ol/layer";
import VectorLayer from "ol/layer/Vector";
import { Cluster, Source } from "ol/source";
import VectorSource from "ol/source/Vector";
import { Fill, Icon, Stroke, Style, Text } from "ol/style";
import IconAnchorUnits from "ol/style/IconAnchorUnits";
import IconOrigin from "ol/style/IconOrigin";
import ferryIcon from "~/assets/img/ferry.svg";
import fleetFerryIcon from "~/assets/img/fleet_ferry.svg";
import { digitrafficAttributions } from "../hooks/useCreateMapLayers";
import { LayerTitle } from "../types/layers";
import { VesselMapItem } from "../types/mapItems";
import { createLayer } from "./createLayer";

export function createAISVesselLayer(fleet = false): Layer<Source> {
  const vesselSource = new VectorSource();
  const vesselSizeStyleCache: {
    [key: string]: Style;
  } = {};
  const vesselClusterStyleCache: {
    [key: number]: Style;
  } = {};
  const vesselsLayer = createLayer({
    source: new Cluster({
      distance: 50,
      source: vesselSource,
      attributions: [digitrafficAttributions],
    }),
    layer: new VectorLayer({
      // Needed for the large icons
      renderBuffer: 400,
      style(feature, resolution) {
        const size: number = feature.get("features")?.length;
        const clustered = size > 1;
        const clusterIcon = fleet
          ? `${window.location.origin}${fleetFerryIcon}`
          : `${window.location.origin}${ferryIcon}`;

        // AIS vessel icons are rotated and scaled according to the actual data
        let heading: number | undefined;
        let refA: number | undefined; // From front to AIS
        let refB: number | undefined; // From AIS to back
        let refC: number | undefined; // From left to AIS
        let refD: number | undefined; // From AIS to right
        let width = 10;
        let length = 24;
        // let scaledWidth = 10;
        // let scaledHeight = 24;
        if (!clustered) {
          const vessel = feature
            .get("features")?.[0]
            ?.getProperties() as VesselMapItem;
          if (vessel) {
            refA = vessel.referencePointA;
            refB = vessel.referencePointB;
            refC = vessel.referencePointC;
            refD = vessel.referencePointD;
            length = refA + refB;
            width = refC + refD;
            // scaledHeight = length / resolution;
            // scaledWidth = width / resolution;
            heading = vessel.heading !== 511 ? vessel.heading : undefined;
          }
        }
        const resolutionCutOff = resolution < 7;

        const calculateSize = resolutionCutOff && width && length && heading;

        const sizeCacheKey = calculateSize
          ? `${resolution},${heading},${refA},${refB},${refC},${refD}`
          : "-";

        let style = clustered
          ? vesselClusterStyleCache[size]
          : vesselSizeStyleCache[sizeCacheKey];

        const aisPosition = resolutionCutOff && width && length && refA && refC;

        // Original icon:
        /*
              <svg version="1.1" width="10" height="24" viewBox="0 0 10 24" id="svg2778" shape-rendering="crispEdges"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:svg="http://www.w3.org/2000/svg">
              <defs id="defs2782" />
              <rect id="rect3629" fill="#3399CC" width="10" height="18" x="0" y="6" ry="0" />
              <path id="path3874" fill="#3399CC" d="M 5,0 10,6 H 0 Z" />
              <circle id="circle1" fill="#eee" r="1" cy="12" cx="5" />
              </svg>
            */
        const iconWidth = width * 2; // Dunno why need multiply by 2 ?????? HACK ToDo ???
        const iconHeight = length * 2;
        const TriangleHeightRatio = 0.6; // Width/triangle height
        const triangleHeight = Math.round(iconWidth * TriangleHeightRatio);
        const rectHeight = Math.round(iconHeight - triangleHeight);
        const circleRadius = iconWidth / 10;
        const circleX = refC ? refC * 2 : iconWidth / 2;
        const circleY = refA ? refA * 2 : iconHeight / 2;

        const iconColor = fleet ? "#BD1CB5" : "#3399CC";

        const dynamicIcon = `data:image/svg+xml;base64,${window.btoa(`<svg version="1.1" width="${iconWidth}" height="${iconHeight}" viewBox="0 0 ${iconWidth} ${iconHeight}" id="svg2778" shape-rendering="crispEdges"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:svg="http://www.w3.org/2000/svg">
              <defs id="defs2782" />
              <rect id="rect3629" fill="${iconColor}" width="${iconWidth}" height="${rectHeight}" x="0" y="${triangleHeight}" ry="0" />
              <path id="path3874" fill="${iconColor}" d="M ${Math.round(
          iconWidth / 2,
        )},0 ${iconWidth},${triangleHeight} H 0 Z" />
              <circle id="circle1" fill="#808080" r="${circleRadius}" cy="${circleY}" cx="${circleX}" />
              </svg>`)}`;

        if (!style) {
          style = new Style({
            image: clustered
              ? new Icon({
                  opacity: 1,
                  src: clusterIcon,
                  scale: 1,
                  offset: [0, 0],
                })
              : new Icon({
                  opacity: 1,
                  src: calculateSize ? dynamicIcon : clusterIcon,
                  scale: calculateSize && width && length ? 1 / resolution : 1,
                  offset: [0, 0],
                  anchorOrigin: aisPosition ? IconOrigin.TOP_LEFT : undefined,
                  anchor: aisPosition
                    ? [refC! / width, refA! / length]
                    : undefined,
                  anchorXUnits: IconAnchorUnits.FRACTION,
                  anchorYUnits: IconAnchorUnits.FRACTION,
                  rotation:
                    calculateSize && heading
                      ? heading * (Math.PI / 180)
                      : undefined,
                }),
            text: clustered
              ? new Text({
                  text: size.toString(),
                  fill: new Fill({
                    color: "#fff",
                  }),
                  font: "18px Roboto",
                  stroke: new Stroke({
                    color: "#000",
                    width: 2,
                  }),
                })
              : undefined,
          });
          if (clustered) {
            vesselClusterStyleCache[size] = style;
          } else {
            vesselSizeStyleCache[sizeCacheKey] = style;
          }
        }
        return style;
      },
    }),
    title: fleet ? LayerTitle.FleetAIS : LayerTitle.Vessels,
  });
  return vesselsLayer;
}
